<script setup lang="ts">
import { computed, ComputedRef, reactive } from 'vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ShiftQuestion from '@/components/ShiftQuestion.vue';
import { CoButton } from '@deputy/copilot2';
import { getTranslations } from '@/lib/util/i18n';
import {
  CustomField,
  CustomFieldData,
  CustomFieldType,
} from '@/lib/request/types/timesheetSetup';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { useTimesheetStore } from '@/stores/timesheets';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-question-form',
});

const props = defineProps<{
  cancelButtonText?: string;
  submitButtonText?: string;
  commentBox?: boolean;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (
    e: 'submit',
    areaId: number,
    shiftQuestionValues: Record<string, string>
  ): void;
}>();

const timesheetSetup = useTimesheetSetupStore();
const timesheets = useTimesheetStore();

const customFields: CustomField[] = timesheetSetup.getAllCustomFields() || [];
const customFieldData: CustomFieldData =
  timesheetSetup.getCustomFieldData() || {};

const isCheckbox: (customField: CustomField) => boolean = (customField) => {
  const type = timesheetSetup.convertCustomFieldTypeIntToString(
    customField.Type
  );
  return (
    type == CustomFieldType.BooleanCheckbox ||
    type == CustomFieldType.BooleanCheckboxNoWithComment ||
    type == CustomFieldType.BooleanCheckboxYesWithComment
  );
};

const shiftQuestionValues = reactive<Record<string, string>>({
  commentBox: timesheets.activeTimesheet?.EmployeeComment ?? '',
  ...customFields.reduce<Record<string, string>>((accumulator, customField) => {
    // prefill with existing values or default values
    accumulator[customField.ApiName] =
      (customFieldData[customField.ApiName] as string) ?? customField.Default;

    // non-mandatory checkbox that has no default values are defaulted to 'No'
    if (
      isCheckbox(customField) &&
      accumulator[customField.ApiName] === '' &&
      !customField.Validation.length
    ) {
      accumulator[customField.ApiName] = 'false';
    }

    // normalise all non-array to string for consistency
    if (!Array.isArray(accumulator[customField.ApiName])) {
      accumulator[customField.ApiName] =
        accumulator[customField.ApiName].toString();
    }

    return accumulator;
  }, {}),
});

const filteredFieldsWithCommentBox: CustomField[] = customFields.filter(
  (customField) => {
    const type = timesheetSetup.convertCustomFieldTypeIntToString(
      customField.Type
    );
    return (
      type == CustomFieldType.BooleanCheckboxNoWithComment ||
      type == CustomFieldType.BooleanCheckboxYesWithComment
    );
  }
);

const hasCommentBox = !!filteredFieldsWithCommentBox.length || props.commentBox;

const isCommentBoxMandatory: ComputedRef<boolean> = computed(() => {
  return !!filteredFieldsWithCommentBox.find((customField: CustomField) => {
    const type = timesheetSetup.convertCustomFieldTypeIntToString(
      customField.Type
    );
    return (
      (type == CustomFieldType.BooleanCheckboxYesWithComment &&
        shiftQuestionValues[customField.ApiName] == 'true') ||
      (type == CustomFieldType.BooleanCheckboxNoWithComment &&
        shiftQuestionValues[customField.ApiName] == 'false')
    );
  });
});

const state = reactive({
  showError: false,
});

function isValid() {
  if (isCommentBoxMandatory.value && shiftQuestionValues.commentBox == '') {
    return false;
  }

  return !customFields.find(
    (customField) =>
      !!customField.Validation.length &&
      (!shiftQuestionValues[customField.ApiName] ||
        !shiftQuestionValues[customField.ApiName]?.length)
  );
}

function handleSubmit() {
  if (isValid()) {
    emit('submit', timesheetSetup.areaId, shiftQuestionValues);
  } else {
    state.showError = true;
  }
}
</script>

<template>
  <LoadingSpinner
    v-if="timesheetSetup.isLoading"
    class="__spinner"
  ></LoadingSpinner>
  <div v-else>
    <ShiftQuestion
      v-for="customField in customFields"
      :key="customField.Id"
      :question-text="customField.Name"
      :help-text="customField.Helptext"
      :type="timesheetSetup.convertCustomFieldTypeIntToString(customField.Type)"
      v-model="shiftQuestionValues[customField.ApiName]"
      :value-options="customField.Valuelist"
      :required="!!customField.Validation.length"
      :show-error="state.showError"
    ></ShiftQuestion>

    <ShiftQuestion
      v-if="hasCommentBox"
      :type="CustomFieldType.LargeText"
      v-model="shiftQuestionValues.commentBox"
      :question-text="t('comment-box-label')"
      :placeholder-text="t('comment-box-placeholder')"
      :required="isCommentBoxMandatory"
      :show-error="state.showError"
      data-test-id="comment-box"
    ></ShiftQuestion>

    <div class="__buttons">
      <CoButton
        v-if="props.cancelButtonText"
        class="__cancel"
        type="default"
        size="large"
        @click="emit('cancel')"
        data-test-id="cancel-button"
      >
        {{ props.cancelButtonText }}
      </CoButton>
      <CoButton
        v-if="props.submitButtonText"
        class="__submit"
        type="primary"
        size="large"
        @click="handleSubmit"
        data-test-id="submit-button"
      >
        {{ props.submitButtonText }}
      </CoButton>
    </div>
  </div>
</template>

<style>
.__spinner {
  display: flex;
  margin: 100px;
}
.__buttons {
  display: flex;
  justify-content: center;
}
.__buttons button {
  width: 100%;
}
</style>
